import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Adresse } from '../../../core/models/adresse.model';
import { BienService } from '../../../core/services/bien.service';
import { DocumentTechniqueService } from '../../../core/services/document-technique.service';
import { FlashService } from '../../../core/services/flash.service';
import { LogementService } from '../../../core/services/logement.service';
import { APP_ROUTES } from '../../../core/constants/app-routes';
import {
  FLASH_MESSAGES,
  UI_MESSAGES,
} from '../../../core/constants/flash-messages';
import {
  BienImmobilier,
  PERIODES_CONSTRUCTION,
  REGIMES_JURIDIQUES,
  TYPES_HABITAT,
  USAGES_BIEN,
  formatAdresse,
} from '../../../core/models/bien-immobilier.model';
import { isDossierTechniqueComplet } from '../../../core/models/document-technique.model';
import {
  Logement,
  TYPE_UNITE,
  TYPES_CHAUFFAGE,
  TYPES_EAU,
  TYPES_PARKING_CHAUFFAGE,
  TYPES_UNITE,
  compareUnites,
  formatDateFr,
  isParking,
  isParkingChauffageValue,
  isUniteFermee,
  statutsFiscauxPourMeuble,
  statutsFiscauxPourNonMeuble,
  toDateIso,
} from '../../../core/models/logement.model';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { I18nValuePipe } from '../../../core/pipes/i18n-value.pipe';
import { DocumentsAchatComponent } from '../documents-achat/documents-achat.component';
import { HelpTipComponent } from '../../../shared/help-tip/help-tip.component';

type BienSection = 'unites' | 'achat' | 'encadrement' | 'communes';
type FormSection = 'achat' | 'encadrement' | 'communes';

function optionalPositiveIntValidator(
  control: AbstractControl
): ValidationErrors | null {
  const raw = control.value;
  if (raw === '' || raw === null || raw === undefined) {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    return { positiveInt: true };
  }
  return null;
}

function toDateInput(value?: Date | string | null): string {
  return toDateIso(value);
}

function numberToInput(value?: number | null): string {
  if (value === undefined || value === null || value === 0) {
    return '';
  }
  return String(value);
}

function includesOption(options: readonly string[], value: string): boolean {
  return options.includes(value);
}

@Component({
    selector: 'app-fiche-bien',
    templateUrl: './fiche-bien.component.html',
    styleUrls: ['./fiche-bien.component.css'],
    imports: [NgTemplateOutlet, RouterLink, FaIconComponent, FormsModule, ReactiveFormsModule, TranslateModule, I18nValuePipe, DocumentsAchatComponent, HelpTipComponent]
})
export class FicheBienComponent implements OnInit {
  @ViewChild('bienDeleteDialog')
  private readonly bienDeleteDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('uniteDeleteDialog')
  private readonly uniteDeleteDialog?: ElementRef<HTMLDialogElement>;

  bien?: BienImmobilier;
  unitesList: Logement[] = [];
  loading = true;
  error = false;
  saving = false;
  editingBien = false;
  bienFormError = false;
  confirmingBienDelete = false;
  deletingBien = false;
  bienDeleteError = false;
  unitToDelete?: Logement;
  deletingId?: string;
  deleteError = false;
  formError = false;
  entierError = false;
  /** Mode appliqué (persistant côté UI / données). */
  loueEnEntier = false;
  /** Valeur de l’interrupteur, en attente d’enregistrement. */
  loueEnEntierDraft = false;
  editingId?: string;
  openSections: Record<BienSection, boolean> = {
    unites: true,
    achat: false,
    encadrement: false,
    communes: false,
  };
  savingSection: FormSection | null = null;
  sectionErrorSection: FormSection | null = null;
  /** Valeurs hors listes figées (données anciennes), injectées à l'ouverture. */
  extraType: string | null = null;
  extraChauffage: string | null = null;
  extraEau: string | null = null;
  extraTypeHabitat: string | null = null;
  extraRegime: string | null = null;
  extraPeriode: string | null = null;
  extraUsage: string | null = null;
  readonly typesUnite = TYPES_UNITE;
  readonly typesChauffage = TYPES_CHAUFFAGE;
  readonly typesParkingChauffage = TYPES_PARKING_CHAUFFAGE;
  readonly typesEau = TYPES_EAU;
  readonly typesHabitat = TYPES_HABITAT;
  readonly regimesJuridiques = REGIMES_JURIDIQUES;
  readonly periodesConstruction = PERIODES_CONSTRUCTION;
  readonly usagesBien = USAGES_BIEN;
  readonly routes = APP_ROUTES;

  readonly bienForm = this.fb.nonNullable.group({
    titre: ['', Validators.required],
    rue: ['', Validators.required],
    codePostal: ['', Validators.required],
    ville: ['', Validators.required],
    complement: [''],
  });

  readonly achatForm = this.fb.nonNullable.group({
    dateAchat: [''],
    dateVente: [''],
    prix: [''],
    fraisNotaire: [''],
    fraisAgence: [''],
    fraisDossier: [''],
    fraisCaution: [''],
  });

  readonly encadrementForm = this.fb.nonNullable.group({
    zoneTendue: [false],
    evolutionMaxLoyer: [false],
    loyerPlafonemment: [false],
    loyerReference: [''],
    loyerMajore: [''],
    complementLoyer: [''],
  });

  readonly communesForm = this.fb.nonNullable.group({
    typeHabitat: [''],
    regime: [''],
    periode: [''],
    anneeConstruction: [''],
    usage: [''],
    partiesCommunes: [''],
    equipementsComm: [''],
  });

  readonly form = this.fb.nonNullable.group({
    typeUnite: ['', Validators.required],
    numero: [''],
    surface: ['', optionalPositiveIntValidator],
    nombrePieces: ['', optionalPositiveIntValidator],
    chauffage: [''],
    eau: [''],
    autreParties: [''],
    meuble: [false],
    statutFiscal: [''],
    dateFinGestion: [''],
    colocation: [false],
    assuranceBailleur: [false],
    montantAnnuelAssurance: [''],
    montantMensuelAssuranceLocataire: [''],
    clauseSolidarite: [''],
  });

  private bienId?: string;
  /** Statut dossier technique par unité : true = à jour. */
  private readonly dossierCompletByLogementId = new Map<string, boolean>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly bienService: BienService,
    private readonly logementService: LogementService,
    private readonly documentTechniqueService: DocumentTechniqueService,
    private readonly flashService: FlashService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder
  ) {
    this.encadrementForm.controls.loyerPlafonemment.valueChanges.subscribe(
      (checked) => this.syncLoyerPlafondFields(checked)
    );
    this.syncLoyerPlafondFields(false);
    this.form.controls.assuranceBailleur.valueChanges.subscribe((checked) =>
      this.syncAssuranceLocataireField(checked)
    );
    this.form.controls.colocation.valueChanges.subscribe((checked) =>
      this.syncColocationFields(checked)
    );
    this.form.controls.meuble.valueChanges.subscribe((meuble) =>
      this.syncStatutFiscalFields(!!meuble)
    );
    this.form.controls.typeUnite.valueChanges.subscribe((type) =>
      this.syncParkingFields(type)
    );
    this.syncColocationFields(false);
    this.syncStatutFiscalFields(false, false);
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error = true;
      this.loading = false;
      return;
    }

    this.bienId = id;
    this.load(true);
  }

  get adresse(): string {
    return this.bien ? formatAdresse(this.bien) : '';
  }

  get unites(): string {
    if (this.loueEnEntier) {
      return this.translate.instant('bien.unites.loueEnEntier');
    }
    const n = this.unitesList.length || this.bien?.nbLogementDto || 0;
    if (n === 0) {
      return this.translate.instant('bien.unites.none');
    }
    if (n === 1) {
      return this.translate.instant('bien.unites.one');
    }
    return this.translate.instant('bien.unites.many', { count: n });
  }

  get unitesPanelTitle(): string {
    if (this.loueEnEntier) {
      return this.translate.instant('logements.fiche.panelLogement');
    }
    return this.unitesList.length > 1
      ? this.translate.instant('logements.fiche.panelUnites')
      : this.translate.instant('logements.fiche.panelLogement');
  }

  get canToggleLoueEnEntier(): boolean {
    return (
      !this.isBusy &&
      !this.isEditing &&
      !this.editingBien &&
      this.unitesList.length <= 1
    );
  }

  /** Texte d’aide du mode loué en entier (explication ou contrainte). */
  get loueEnEntierHelpKey(): string {
    return this.unitesList.length > 1
      ? 'logements.fiche.loueEnEntierLocked'
      : 'logements.fiche.loueEnEntierHint';
  }

  get loueEnEntierDirty(): boolean {
    return this.loueEnEntierDraft !== this.loueEnEntier;
  }

  get isEditing(): boolean {
    return !!this.editingId;
  }

  get isBusy(): boolean {
    return (
      this.saving ||
      !!this.deletingId ||
      this.deletingBien ||
      !!this.savingSection
    );
  }

  get isLastUnite(): boolean {
    return this.unitesList.length <= 1;
  }

  get submitLabel(): string {
    if (this.saving) {
      return this.translate.instant('common.saving');
    }
    return this.isEditing
      ? this.translate.instant('common.save')
      : this.translate.instant('common.add');
  }

  get saveErrorMessage(): string {
    return this.isEditing
      ? UI_MESSAGES.impossibleEnregistrerModifications
      : UI_MESSAGES.impossibleAjouterUnite;
  }

  isEditingUnite(unite: Logement): boolean {
    return !!this.editingId && unite.idLogementDto === this.editingId;
  }

  isSectionOpen(section: BienSection): boolean {
    return this.openSections[section];
  }

  sectionSaveLabel(section: FormSection): string {
    return this.savingSection === section
      ? this.translate.instant('common.saving')
      : this.translate.instant('common.save');
  }

  showSectionError(section: FormSection): boolean {
    return this.sectionErrorSection === section;
  }

  showBienError(
    controlName: 'titre' | 'rue' | 'codePostal' | 'ville'
  ): boolean {
    const control = this.bienForm.controls[controlName];
    return control.invalid && control.touched;
  }

  showEncadrementError(
    controlName: 'loyerReference' | 'loyerMajore'
  ): boolean {
    const control = this.encadrementForm.controls[controlName];
    return control.invalid && control.touched;
  }

  showError(controlName: 'typeUnite' | 'surface' | 'nombrePieces'): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && control.touched;
  }

  errorMessage(controlName: 'typeUnite' | 'surface' | 'nombrePieces'): string {
    const control = this.form.controls[controlName];
    if (control.hasError('required')) {
      return this.translate.instant('logements.fiche.typeRequired');
    }
    if (control.hasError('positiveInt')) {
      return controlName === 'surface'
        ? this.translate.instant('logements.fiche.surfaceRequired')
        : this.translate.instant('logements.fiche.piecesRequired');
    }
    return '';
  }

  label(logement: Logement): string {
    const type = logement.typeUniteDto?.trim();
    const typeLabel = type ? this.i18nRef('ref.typeUnite', type) : '';
    const parts = [typeLabel, logement.numeroDto].filter(
      (part) => typeof part === 'string' && part.length > 0
    );
    return (
      parts.join(' · ') || this.translate.instant('bien.logementFallback')
    );
  }

  openDossierTechnique(unite: Logement): void {
    const bienId = this.bien?.idBienDto;
    const logementId = unite.idLogementDto;
    if (!bienId || !logementId) {
      return;
    }
    void this.router.navigateByUrl(
      APP_ROUTES.dossierTechnique(bienId, logementId)
    );
  }

  hasDossierStatus(unite: Logement): boolean {
    return (
      !!unite.idLogementDto &&
      this.dossierCompletByLogementId.has(unite.idLogementDto)
    );
  }

  dossierComplet(unite: Logement): boolean {
    const id = unite.idLogementDto;
    if (!id) {
      return false;
    }
    return this.dossierCompletByLogementId.get(id) === true;
  }

  details(logement: Logement): string {
    const parts: string[] = [];
    const parking = isParking(logement.typeUniteDto);
    if (logement.surfaceDto) {
      parts.push(
        this.translate.instant('bien.surface', { value: logement.surfaceDto })
      );
    }
    if (!parking && logement.nombrePiecesDto) {
      const n = logement.nombrePiecesDto;
      parts.push(
        n === 1
          ? this.translate.instant('bien.pieces.one')
          : this.translate.instant('bien.pieces.many', { count: n })
      );
    }
    if (logement.chauffageDto) {
      parts.push(this.i18nRef('ref.chauffage', logement.chauffageDto));
    }
    if (!parking) {
      parts.push(
        this.translate.instant(
          logement.meubleDto ? 'bien.meuble' : 'bien.nonMeuble'
        )
      );
    }
    if (!parking && logement.colocationDto) {
      parts.push(this.translate.instant('bien.colocation'));
    }
    if (isUniteFermee(logement)) {
      parts.push(
        this.translate.instant('bien.fermeeLe', {
          date: formatDateFr(logement.dateFinGestionDto),
        })
      );
    }
    return parts.join(' · ');
  }

  private i18nRef(prefix: string, value: string): string {
    const key = `${prefix}.${value}`;
    const translated = this.translate.instant(key);
    return translated === key ? value : translated;
  }

  isFermee(logement: Logement): boolean {
    return isUniteFermee(logement);
  }

  toggleSection(section: BienSection): void {
    if (this.isBusy) {
      return;
    }

    const willOpen = !this.openSections[section];
    this.openSections = {
      ...this.openSections,
      [section]: willOpen,
    };

    if (section === 'unites' && !willOpen && this.isEditing) {
      this.cancelEdit();
    }

    if (willOpen && section !== 'unites') {
      this.sectionErrorSection = null;
      this.fillSectionForm(section);
    }
  }

  submitSection(section: FormSection): void {
    if (this.isBusy || !this.bien || !this.bienId) {
      return;
    }

    if (section === 'encadrement' && this.encadrementForm.invalid) {
      this.encadrementForm.markAllAsTouched();
      this.sectionErrorSection = null;
      return;
    }

    const payload = this.buildSectionPayload(section);
    this.savingSection = section;
    this.sectionErrorSection = null;

    this.bienService.update(payload).subscribe({
      next: () => {
        this.savingSection = null;
        this.flashService.success(this.sectionFlashMessage(section));
        this.load();
      },
      error: () => {
        this.savingSection = null;
        this.sectionErrorSection = section;
      },
    });
  }

  startEditBien(): void {
    if (!this.bien || this.isBusy || this.isEditing) {
      return;
    }

    this.cancelDelete();
    this.cancelBienDelete();
    this.openSections = {
      ...this.openSections,
      unites: true,
    };
    this.editingBien = true;
    this.bienFormError = false;
    const adresse = this.bien.adresseDto;
    this.bienForm.reset({
      titre: this.bien.titreDto ?? '',
      rue: adresse?.rueDto ?? '',
      codePostal: adresse?.codePostalDto ?? '',
      ville: adresse?.villeDto ?? '',
      complement: adresse?.complementDto ?? '',
    });
  }

  cancelEditBien(): void {
    this.editingBien = false;
    this.bienFormError = false;
    this.bienForm.reset({
      titre: '',
      rue: '',
      codePostal: '',
      ville: '',
      complement: '',
    });
  }

  askDeleteBien(): void {
    if (!this.bien || this.isBusy || this.isEditing || this.editingBien) {
      return;
    }

    this.cancelDelete();
    this.bienDeleteError = false;
    this.confirmingBienDelete = true;
    queueMicrotask(() => this.bienDeleteDialog?.nativeElement.showModal());
  }

  cancelBienDelete(): void {
    if (this.deletingBien) {
      return;
    }
    this.bienDeleteDialog?.nativeElement.close();
    this.confirmingBienDelete = false;
  }

  onBienDeleteDialogCancel(event: Event): void {
    if (this.deletingBien) {
      event.preventDefault();
      return;
    }
    this.confirmingBienDelete = false;
  }

  onUniteDeleteDialogCancel(event: Event): void {
    if (this.deletingId) {
      event.preventDefault();
      return;
    }
    this.unitToDelete = undefined;
  }

  onDialogBackdropClick(event: MouseEvent, target: 'bien' | 'unite'): void {
    this.closeDialogFromBackdrop(event, target);
  }

  onDialogBackdropKeydown(
    event: KeyboardEvent,
    target: 'bien' | 'unite'
  ): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.closeDialogFromBackdrop(event, target);
  }

  private closeDialogFromBackdrop(
    event: Event,
    target: 'bien' | 'unite'
  ): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (target === 'bien') {
      this.cancelBienDelete();
      return;
    }
    this.cancelDelete();
  }

  confirmDeleteBien(): void {
    if (!this.bienId || this.isBusy || !this.confirmingBienDelete) {
      return;
    }

    this.deletingBien = true;
    this.bienDeleteError = false;

    this.bienService.delete(this.bienId).subscribe({
      next: () => {
        this.deletingBien = false;
        this.bienDeleteDialog?.nativeElement.close();
        this.confirmingBienDelete = false;
        this.flashService.success(FLASH_MESSAGES.bienSupprime);
        void this.router.navigate([APP_ROUTES.logements]);
      },
      error: () => {
        this.deletingBien = false;
        this.bienDeleteError = true;
      },
    });
  }

  submitBien(): void {
    if (this.isBusy || !this.bien || !this.bienId) {
      return;
    }

    if (this.bienForm.invalid) {
      this.bienForm.markAllAsTouched();
      this.bienFormError = false;
      return;
    }

    const payload = this.buildBienIdentityPayload();
    this.saving = true;
    this.bienFormError = false;

    this.bienService.update(payload).subscribe({
      next: () => {
        this.saving = false;
        this.cancelEditBien();
        this.flashService.success(FLASH_MESSAGES.bienMisAJour);
        this.load();
      },
      error: () => {
        this.saving = false;
        this.bienFormError = true;
      },
    });
  }

  startEdit(unite: Logement): void {
    if (
      !unite.idLogementDto ||
      this.isBusy ||
      this.editingBien ||
      isUniteFermee(unite)
    ) {
      return;
    }

    this.cancelDelete();
    this.cancelBienDelete();
    this.entierError = false;
    this.openSections = {
      ...this.openSections,
      unites: true,
    };

    const type = this.resolveEditType(unite);
    const parking = isParking(type);
    this.applyExtraUniteOptions(unite, type, parking);
    this.editingId = unite.idLogementDto;
    this.formError = false;
    this.deleteError = false;
    const values = this.buildUniteFormValues(unite, type, parking);
    const editId = unite.idLogementDto;

    // Après masquage du formulaire d'ajout (*ngIf), sinon sa destruction
    // peut réécrire typeUnite à vide.
    setTimeout(() => this.applyEditForm(editId, values));
  }

  private resolveEditType(unite: Logement): string {
    if (this.loueEnEntier) {
      return TYPE_UNITE.MAISON;
    }
    return unite.typeUniteDto ?? '';
  }

  private applyExtraUniteOptions(
    unite: Logement,
    type: string,
    parking: boolean
  ): void {
    this.extraType = this.extraOptionOutside(
      !this.loueEnEntier ? type : '',
      TYPES_UNITE
    );
    this.extraChauffage = parking
      ? null
      : this.extraOptionOutside(unite.chauffageDto ?? '', TYPES_CHAUFFAGE);
    this.extraEau = parking
      ? null
      : this.extraOptionOutside(unite.eauDto ?? '', TYPES_EAU);
  }

  private extraOptionOutside(
    value: string,
    options: readonly string[]
  ): string | null {
    if (!value || includesOption(options, value)) {
      return null;
    }
    return value;
  }

  private buildUniteFormValues(
    unite: Logement,
    type: string,
    parking: boolean
  ) {
    const chauffage = unite.chauffageDto ?? '';
    const eau = unite.eauDto ?? '';
    return {
      typeUnite: type,
      numero: this.loueEnEntier ? '' : unite.numeroDto ?? '',
      surface: unite.surfaceDto ? String(unite.surfaceDto) : '',
      nombrePieces: this.optionalNumericField(
        parking,
        unite.nombrePiecesDto
      ),
      chauffage: this.chauffageForForm(parking, chauffage),
      eau: parking ? '' : eau,
      autreParties: unite.autrePartiesDto ?? '',
      meuble: parking ? false : !!unite.meubleDto,
      statutFiscal: unite.statutFiscalDto ?? '',
      dateFinGestion: toDateIso(unite.dateFinGestionDto),
      colocation: parking ? false : !!unite.colocationDto,
      assuranceBailleur: parking ? false : !!unite.assuranceBailleurDto,
      montantAnnuelAssurance: this.optionalNumericField(
        parking,
        unite.montantAnnuelAssuranceDto
      ),
      montantMensuelAssuranceLocataire: this.optionalNumericField(
        parking,
        unite.montantMensuelAssuranceLocataireDto
      ),
      clauseSolidarite: parking ? '' : unite.clauseSolidariteDto ?? '',
    };
  }

  /** Valeur chauffage affichée en édition (parking = Chauffé / Non chauffé uniquement). */
  private chauffageForForm(parking: boolean, chauffage: string): string {
    if (!parking) {
      return chauffage;
    }
    return isParkingChauffageValue(chauffage) ? chauffage : '';
  }

  private optionalNumericField(
    clear: boolean,
    value?: number | null
  ): string {
    if (clear || !value) {
      return '';
    }
    return String(value);
  }

  private applyEditForm(
    expectedId: string,
    values: ReturnType<FicheBienComponent['buildUniteFormValues']>
  ): void {
    if (this.editingId !== expectedId) {
      return;
    }
    this.form.reset(values);
    this.syncColocationFields(!!values.colocation, false);
    this.syncParkingFields(values.typeUnite);
    this.syncStatutFiscalFields(!!values.meuble, false);
  }

  /** Bascule uniquement le brouillon : à valider via Enregistrer. */
  toggleLoueEnEntierDraft(): void {
    if (!this.canToggleLoueEnEntier) {
      return;
    }

    this.cancelDelete();
    this.cancelBienDelete();
    this.entierError = false;
    this.loueEnEntierDraft = !this.loueEnEntierDraft;
  }

  onLoueEnEntierChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!this.canToggleLoueEnEntier) {
      input.checked = this.loueEnEntierDraft;
      return;
    }

    this.cancelDelete();
    this.cancelBienDelete();
    this.entierError = false;
    this.loueEnEntierDraft = input.checked;
  }

  cancelLoueEnEntierDraft(): void {
    if (this.isBusy) {
      return;
    }
    this.loueEnEntierDraft = this.loueEnEntier;
    this.entierError = false;
  }

  /** Applique le mode « loué en entier » choisi sur l’interrupteur. */
  saveLoueEnEntierMode(): void {
    if (
      !this.loueEnEntierDirty ||
      this.isBusy ||
      this.isEditing ||
      this.editingBien
    ) {
      return;
    }

    this.cancelDelete();
    this.cancelBienDelete();
    this.entierError = false;

    if (!this.loueEnEntierDraft) {
      this.persistLoueEnEntierFlag(false, 'logements.fiche.modeUnitesSeparees');
      return;
    }

    if (this.unitesList.length > 1) {
      this.entierError = true;
      return;
    }

    if (this.unitesList.length === 1) {
      const unite = this.unitesList[0];
      if (this.looksLikeLoueEnEntier(unite)) {
        this.persistLoueEnEntierFlag(true, FLASH_MESSAGES.bienLoueEnEntier);
        return;
      }
      this.alignUnitePourLoueEnEntier(unite);
      return;
    }

    this.createUnitePourLoueEnEntier();
  }

  cancelEdit(): void {
    this.editingId = undefined;
    this.extraType = null;
    this.formError = false;
    this.resetForm();
  }

  /** Annule la fin de gestion pour rendre l'unité à nouveau modifiable. */
  reopenUnite(unite: Logement): void {
    if (
      !unite.idLogementDto ||
      this.isBusy ||
      this.isEditing ||
      this.editingBien ||
      !isUniteFermee(unite)
    ) {
      return;
    }

    this.cancelDelete();
    this.cancelBienDelete();

    const logement = new Logement();
    logement.idLogementDto = unite.idLogementDto;
    logement.typeUniteDto = unite.typeUniteDto;
    logement.numeroDto = unite.numeroDto;
    logement.surfaceDto = unite.surfaceDto ?? 0;
    logement.nombrePiecesDto = unite.nombrePiecesDto ?? 0;
    logement.chauffageDto = unite.chauffageDto;
    logement.eauDto = unite.eauDto;
    logement.autrePartiesDto = unite.autrePartiesDto;
    logement.meubleDto = unite.meubleDto;
    logement.statutFiscalDto = unite.statutFiscalDto;
    logement.dateFinGestionDto = undefined;
    logement.colocationDto = unite.colocationDto;
    logement.assuranceBailleurDto = unite.assuranceBailleurDto;
    logement.montantAnnuelAssuranceDto = unite.montantAnnuelAssuranceDto;
    logement.montantMensuelAssuranceLocataireDto =
      unite.montantMensuelAssuranceLocataireDto;
    logement.clauseSolidariteDto = unite.clauseSolidariteDto;
    logement.clauseResolutoireDto = unite.clauseResolutoireDto;

    this.saving = true;
    this.logementService.updateLogement(logement).subscribe({
      next: () => {
        this.saving = false;
        this.flashService.success(FLASH_MESSAGES.gestionUniteReouverte);
        this.load();
      },
      error: () => {
        this.saving = false;
        this.formError = true;
      },
    });
  }

  askDelete(unite: Logement): void {
    if (!unite.idLogementDto || this.isBusy || this.isEditing || this.editingBien) {
      return;
    }

    this.cancelBienDelete();
    this.deleteError = false;
    this.unitToDelete = unite;
    queueMicrotask(() => this.uniteDeleteDialog?.nativeElement.showModal());
  }

  cancelDelete(): void {
    if (this.deletingId) {
      return;
    }
    this.uniteDeleteDialog?.nativeElement.close();
    this.unitToDelete = undefined;
  }

  confirmDelete(): void {
    const id = this.unitToDelete?.idLogementDto;
    if (!id || this.isBusy || this.isEditing) {
      return;
    }

    this.deleteUnite(id, this.isLastUnite);
  }

  submit(): void {
    if (this.isBusy || this.editingBien) {
      return;
    }

    if (this.loueEnEntier) {
      this.form.controls.typeUnite.setValue(TYPE_UNITE.MAISON);
      this.form.controls.numero.setValue('');
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.formError = false;
      return;
    }

    if (this.isEditing) {
      this.saveEdit();
      return;
    }

    this.saveCreate();
  }

  private fillSectionForm(section: FormSection): void {
    if (!this.bien) {
      return;
    }

    if (section === 'achat') {
      this.achatForm.reset({
        dateAchat: toDateInput(this.bien.dateAchatDto),
        dateVente: toDateInput(this.bien.dateVenteDto),
        prix: numberToInput(this.bien.prixDto),
        fraisNotaire: numberToInput(this.bien.fraisNotaireDto),
        fraisAgence: numberToInput(this.bien.fraisAgenceDto),
        fraisDossier: numberToInput(this.bien.fraisDossierDto),
        fraisCaution: numberToInput(this.bien.fraisCautionDto),
      });
      return;
    }

    if (section === 'encadrement') {
      this.encadrementForm.reset({
        zoneTendue: !!this.bien.zoneTendueDto,
        evolutionMaxLoyer: !!this.bien.evolutionMaxLoyerDto,
        loyerPlafonemment: !!this.bien.loyerPlafonemmentDto,
        loyerReference: numberToInput(this.bien.loyerReferenceDto),
        loyerMajore: numberToInput(this.bien.loyerMajoreDto),
        complementLoyer: this.bien.complementLoyerDto ?? '',
      });
      this.syncLoyerPlafondFields(
        !!this.bien.loyerPlafonemmentDto,
        false
      );
      return;
    }

    this.communesForm.reset({
      typeHabitat: this.bien.typeHabitatDto ?? '',
      regime: this.bien.regimeDto ?? '',
      periode: this.bien.periodeDto ?? '',
      anneeConstruction: numberToInput(this.bien.anneeConstructionDto),
      usage: this.bien.usageDto ?? '',
      partiesCommunes: this.bien.partiesCommunesDto ?? '',
      equipementsComm: this.bien.equipementsCommDto ?? '',
    });

    const typeHabitat = this.bien.typeHabitatDto ?? '';
    this.extraTypeHabitat =
      typeHabitat && !includesOption(TYPES_HABITAT, typeHabitat)
        ? typeHabitat
        : null;

    const regime = this.bien.regimeDto ?? '';
    this.extraRegime =
      regime && !includesOption(REGIMES_JURIDIQUES, regime) ? regime : null;

    const periode = this.bien.periodeDto ?? '';
    this.extraPeriode =
      periode && !includesOption(PERIODES_CONSTRUCTION, periode)
        ? periode
        : null;

    const usage = this.bien.usageDto ?? '';
    this.extraUsage =
      usage && !includesOption(USAGES_BIEN, usage) ? usage : null;
  }

  private buildSectionPayload(section: FormSection): BienImmobilier {
    const bien = Object.assign(new BienImmobilier(), this.bien);
    bien.idBienDto = this.bienId;
    bien.logementsDto = undefined;

    if (section === 'achat') {
      const value = this.achatForm.getRawValue();
      bien.dateAchatDto = value.dateAchat || undefined;
      bien.dateVenteDto = value.dateVente || undefined;
      bien.prixDto = this.optionalNumber(value.prix);
      bien.fraisNotaireDto = this.optionalNumber(value.fraisNotaire);
      bien.fraisAgenceDto = this.optionalNumber(value.fraisAgence);
      bien.fraisDossierDto = this.optionalNumber(value.fraisDossier);
      bien.fraisCautionDto = this.optionalNumber(value.fraisCaution);
      return bien;
    }

    if (section === 'encadrement') {
      const value = this.encadrementForm.getRawValue();
      bien.zoneTendueDto = value.zoneTendue;
      bien.evolutionMaxLoyerDto = value.evolutionMaxLoyer;
      bien.loyerPlafonemmentDto = value.loyerPlafonemment;
      if (value.loyerPlafonemment) {
        bien.loyerReferenceDto = this.optionalNumber(value.loyerReference);
        bien.loyerMajoreDto = this.optionalNumber(value.loyerMajore);
        bien.complementLoyerDto = value.complementLoyer.trim() || undefined;
      } else {
        bien.loyerReferenceDto = 0;
        bien.loyerMajoreDto = 0;
        bien.complementLoyerDto = undefined;
      }
      return bien;
    }

    const value = this.communesForm.getRawValue();
    bien.typeHabitatDto = value.typeHabitat.trim() || undefined;
    bien.regimeDto = value.regime.trim() || undefined;
    bien.periodeDto = value.periode.trim() || undefined;
    bien.anneeConstructionDto = this.optionalInteger(value.anneeConstruction);
    bien.usageDto = value.usage.trim() || undefined;
    bien.partiesCommunesDto = value.partiesCommunes.trim() || undefined;
    bien.equipementsCommDto = value.equipementsComm.trim() || undefined;
    return bien;
  }

  private sectionFlashMessage(section: FormSection): string {
    if (section === 'achat') {
      return 'logements.fiche.flashAchat';
    }
    if (section === 'encadrement') {
      return 'logements.fiche.flashEncadrement';
    }
    return 'logements.fiche.flashCaracteristiques';
  }

  private buildBienIdentityPayload(): BienImmobilier {
    const value = this.bienForm.getRawValue();
    const bien = Object.assign(new BienImmobilier(), this.bien);
    bien.idBienDto = this.bienId;
    bien.titreDto = value.titre.trim();

    const adresse = new Adresse();
    adresse.rueDto = value.rue.trim();
    adresse.codePostalDto = value.codePostal.trim();
    adresse.villeDto = value.ville.trim();
    adresse.complementDto = value.complement.trim() || undefined;
    bien.adresseDto = adresse;
    bien.logementsDto = undefined;
    return bien;
  }

  private deleteUnite(id: string, isLast: boolean): void {
    this.deletingId = id;
    this.deleteError = false;

    this.logementService.deleteLogement(id).subscribe({
      next: () => {
        this.deletingId = undefined;
        this.uniteDeleteDialog?.nativeElement.close();
        this.unitToDelete = undefined;
        if (isLast) {
          this.flashService.success(FLASH_MESSAGES.uniteEtBienSupprimes);
          void this.router.navigate([APP_ROUTES.logements]);
          return;
        }
        this.flashService.success(FLASH_MESSAGES.uniteSupprimee);
        this.load();
      },
      error: () => {
        this.deletingId = undefined;
        this.deleteError = true;
      },
    });
  }

  private saveCreate(): void {
    if (!this.bienId) {
      return;
    }

    this.cancelDelete();
    const logement = this.buildPayload();
    this.saving = true;
    this.formError = false;

    this.bienService.addLogement(this.bienId, logement).subscribe({
      next: () => {
        this.resetForm();
        this.saving = false;
        this.load();
      },
      error: () => {
        this.saving = false;
        this.formError = true;
      },
    });
  }

  private saveEdit(): void {
    if (!this.editingId) {
      return;
    }

    const logement = this.buildPayload();
    logement.idLogementDto = this.editingId;
    this.saving = true;
    this.formError = false;

    this.logementService.updateLogement(logement).subscribe({
      next: () => {
        this.cancelEdit();
        this.saving = false;
        this.flashService.success(FLASH_MESSAGES.uniteMiseAJour);
        this.load();
      },
      error: () => {
        this.saving = false;
        this.formError = true;
      },
    });
  }

  private buildPayload(): Logement {
    const value = this.form.getRawValue();
    const logement = new Logement();
    this.applyTypeAndNumeroToPayload(logement, value);
    logement.surfaceDto = this.optionalPositiveInt(value.surface) ?? 0;
    const parking = isParking(logement.typeUniteDto);
    logement.nombrePiecesDto = parking
      ? 0
      : this.optionalPositiveInt(value.nombrePieces) ?? 0;
    logement.chauffageDto = this.chauffageForPayload(
      parking,
      value.chauffage.trim()
    );
    logement.eauDto = parking ? undefined : value.eau.trim() || undefined;
    logement.autrePartiesDto = value.autreParties.trim() || undefined;
    logement.meubleDto = parking ? false : value.meuble;
    logement.statutFiscalDto = value.statutFiscal.trim() || undefined;
    logement.dateFinGestionDto = value.dateFinGestion || undefined;
    logement.colocationDto = parking ? false : value.colocation;
    this.applyColocationToPayload(logement, value);
    if (this.editingId) {
      const existing = this.unitesList.find(
        (item) => item.idLogementDto === this.editingId
      );
      logement.clauseResolutoireDto = existing?.clauseResolutoireDto;
    }
    return logement;
  }

  private applyTypeAndNumeroToPayload(
    logement: Logement,
    value: ReturnType<FicheBienComponent['form']['getRawValue']>
  ): void {
    if (this.loueEnEntier) {
      logement.typeUniteDto = TYPE_UNITE.MAISON;
      logement.numeroDto = undefined;
      return;
    }
    logement.typeUniteDto = value.typeUnite.trim();
    logement.numeroDto = value.numero.trim() || undefined;
  }

  private chauffageForPayload(
    parking: boolean,
    chauffage: string
  ): string | undefined {
    if (parking) {
      return isParkingChauffageValue(chauffage) ? chauffage : undefined;
    }
    return chauffage || undefined;
  }

  private applyColocationToPayload(
    logement: Logement,
    value: ReturnType<FicheBienComponent['form']['getRawValue']>
  ): void {
    const colocation = !!logement.colocationDto;
    logement.assuranceBailleurDto = colocation
      ? value.assuranceBailleur
      : false;
    const withAssurance = colocation && value.assuranceBailleur;
    logement.montantAnnuelAssuranceDto = withAssurance
      ? this.optionalNumber(value.montantAnnuelAssurance)
      : 0;
    logement.montantMensuelAssuranceLocataireDto = withAssurance
      ? this.optionalNumber(value.montantMensuelAssuranceLocataire)
      : 0;
    logement.clauseSolidariteDto = colocation
      ? value.clauseSolidarite.trim() || undefined
      : undefined;
  }

  private resetForm(): void {
    this.extraType = null;
    this.extraChauffage = null;
    this.extraEau = null;
    this.form.reset({
      typeUnite: '',
      numero: '',
      surface: '',
      nombrePieces: '',
      chauffage: '',
      eau: '',
      autreParties: '',
      meuble: false,
      statutFiscal: '',
      dateFinGestion: '',
      colocation: false,
      assuranceBailleur: false,
      montantAnnuelAssurance: '',
      montantMensuelAssuranceLocataire: '',
      clauseSolidarite: '',
    });
    this.syncColocationFields(false);
    this.syncStatutFiscalFields(false, false);
  }

  private looksLikeLoueEnEntier(unite: Logement): boolean {
    return unite.typeUniteDto === TYPE_UNITE.MAISON && !unite.numeroDto?.trim();
  }

  private syncLoueEnEntierFromData(): void {
    this.loueEnEntier = !!this.bien?.loueEnEntierDto;
    this.loueEnEntierDraft = this.loueEnEntier;
  }

  private persistLoueEnEntierFlag(
    enabled: boolean,
    successMessage: string,
    after?: () => void
  ): void {
    if (!this.bien || !this.bienId) {
      return;
    }

    const bien = Object.assign(new BienImmobilier(), this.bien);
    bien.idBienDto = this.bienId;
    bien.loueEnEntierDto = enabled;
    bien.logementsDto = undefined;
    this.saving = true;
    this.entierError = false;

    this.bienService.update(bien).subscribe({
      next: () => {
        this.saving = false;
        if (this.bien) {
          this.bien.loueEnEntierDto = enabled;
        }
        this.loueEnEntier = enabled;
        this.loueEnEntierDraft = enabled;
        this.flashService.success(successMessage);
        after?.();
      },
      error: () => {
        this.saving = false;
        this.entierError = true;
      },
    });
  }

  private createUnitePourLoueEnEntier(): void {
    if (!this.bienId) {
      return;
    }

    const logement = new Logement();
    logement.typeUniteDto = TYPE_UNITE.MAISON;
    this.saving = true;

    this.bienService.addLogement(this.bienId, logement).subscribe({
      next: (created) => this.afterUniteCreatedForLoueEnEntier(created),
      error: () => {
        this.saving = false;
        this.entierError = true;
      },
    });
  }

  private afterUniteCreatedForLoueEnEntier(created: Logement): void {
    this.persistLoueEnEntierFlag(true, FLASH_MESSAGES.bienLoueEnEntier, () =>
      this.openCreatedUnite(created)
    );
  }

  private openCreatedUnite(created: Logement): void {
    this.load(false, () => this.startEditCreatedUnite(created));
  }

  private startEditCreatedUnite(created: Logement): void {
    const id = created.idLogementDto;
    const unite =
      this.unitesList.find((item) => item.idLogementDto === id) ??
      this.unitesList[0];
    if (unite) {
      this.startEdit(unite);
    }
  }

  private alignUnitePourLoueEnEntier(unite: Logement): void {
    if (!unite.idLogementDto) {
      return;
    }

    const logement = new Logement();
    logement.idLogementDto = unite.idLogementDto;
    logement.typeUniteDto = TYPE_UNITE.MAISON;
    logement.numeroDto = undefined;
    logement.surfaceDto = unite.surfaceDto ?? 0;
    logement.nombrePiecesDto = unite.nombrePiecesDto ?? 0;
    logement.chauffageDto = unite.chauffageDto;
    logement.eauDto = unite.eauDto;
    logement.autrePartiesDto = unite.autrePartiesDto;
    logement.meubleDto = unite.meubleDto;
    logement.statutFiscalDto = unite.statutFiscalDto;
    logement.dateFinGestionDto = unite.dateFinGestionDto;
    logement.colocationDto = unite.colocationDto;
    logement.assuranceBailleurDto = unite.assuranceBailleurDto;
    logement.montantAnnuelAssuranceDto = unite.montantAnnuelAssuranceDto;
    logement.montantMensuelAssuranceLocataireDto =
      unite.montantMensuelAssuranceLocataireDto;
    logement.clauseSolidariteDto = unite.clauseSolidariteDto;
    logement.clauseResolutoireDto = unite.clauseResolutoireDto;
    this.saving = true;

    this.logementService.updateLogement(logement).subscribe({
      next: () => {
        this.persistLoueEnEntierFlag(true, FLASH_MESSAGES.bienLoueEnEntier, () => {
          this.load();
        });
      },
      error: () => {
        this.saving = false;
        this.entierError = true;
      },
    });
  }

  private load(initial = false, after?: () => void): void {
    if (!this.bienId) {
      return;
    }

    this.bienService.findById(this.bienId).subscribe({
      next: (bien) => {
        this.bien = bien;
        this.unitesList = [...(bien.logementsDto ?? [])].sort(compareUnites);
        this.syncLoueEnEntierFromData();
        this.loading = false;
        this.error = false;
        this.loadDossierStatuses();
        const openFormSections: FormSection[] = [
          'achat',
          'encadrement',
          'communes',
        ];
        openFormSections.forEach((section) => {
          if (this.openSections[section]) {
            this.fillSectionForm(section);
          }
        });
        after?.();
      },
      error: () => {
        if (initial) {
          this.error = true;
          this.loading = false;
        }
      },
    });
  }

  private loadDossierStatuses(): void {
    const annee = this.bien?.anneeConstructionDto;
    const logements = this.unitesList.filter((item) => !!item.idLogementDto);
    this.dossierCompletByLogementId.clear();
    if (logements.length === 0) {
      return;
    }

    forkJoin(
      logements.map((logement) => {
        const id = logement.idLogementDto;
        return this.documentTechniqueService.list(id).pipe(
          map((docs) => ({
            id,
            complet: isDossierTechniqueComplet(
              docs ?? [],
              annee,
              logement.typeUniteDto,
              logement.chauffageDto
            ),
          })),
          catchError(() => of({ id, complet: false }))
        );
      })
    ).subscribe((results) => {
      for (const result of results) {
        this.dossierCompletByLogementId.set(result.id, result.complet);
      }
    });
  }

  private optionalPositiveInt(raw: string | number): number | undefined {
    if (raw === '' || raw === null || raw === undefined) {
      return undefined;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      return undefined;
    }
    return Math.round(n);
  }

  private optionalNumber(raw: string | number): number {
    if (raw === '' || raw === null || raw === undefined) {
      return 0;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  /** Champ entier optionnel : vide → undefined (pas 0). */
  private optionalInteger(raw: string | number): number | undefined {
    if (raw === '' || raw === null || raw === undefined) {
      return undefined;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? Math.trunc(n) : undefined;
  }

  get isParking(): boolean {
    return isParking(this.form.controls.typeUnite.value);
  }

  get typesStatutFiscal(): readonly string[] {
    if (this.isParking || !this.form.controls.meuble.value) {
      return statutsFiscauxPourNonMeuble();
    }
    return statutsFiscauxPourMeuble();
  }

  /** Parking : champs habitation absents ; conserve chauffé / non chauffé. */
  private syncParkingFields(typeUnite: string): void {
    if (isParking(typeUnite)) {
      const chauffage = (this.form.controls.chauffage.value ?? '').trim();
      this.form.patchValue(
        {
          nombrePieces: '',
          chauffage: isParkingChauffageValue(chauffage) ? chauffage : '',
          eau: '',
          meuble: false,
          colocation: false,
        },
        { emitEvent: false }
      );
      this.extraChauffage = null;
      this.extraEau = null;
      this.syncColocationFields(false);
      this.syncStatutFiscalFields(false);
      return;
    }

    const chauffage = (this.form.controls.chauffage.value ?? '').trim();
    if (isParkingChauffageValue(chauffage)) {
      this.form.patchValue({ chauffage: '' }, { emitEvent: false });
    }
  }

  /** Réinitialise le statut fiscal s'il n'est plus valide pour le mode meublé. */
  private syncStatutFiscalFields(
    meuble: boolean,
    clearIfInvalid = true
  ): void {
    if (!clearIfInvalid) {
      return;
    }
    const current = (this.form.controls.statutFiscal.value ?? '').trim();
    if (!current) {
      return;
    }
    const allowed = meuble
      ? statutsFiscauxPourMeuble()
      : statutsFiscauxPourNonMeuble();
    if (!allowed.includes(current)) {
      this.form.patchValue({ statutFiscal: '' }, { emitEvent: false });
    }
  }

  private syncColocationFields(
    enabled: boolean,
    clearWhenDisabled = true
  ): void {
    if (!enabled) {
      if (clearWhenDisabled) {
        this.form.patchValue(
          {
            assuranceBailleur: false,
            montantAnnuelAssurance: '',
            montantMensuelAssuranceLocataire: '',
            clauseSolidarite: '',
          },
          { emitEvent: false }
        );
      }
      this.syncAssuranceLocataireField(false, false);
      return;
    }

    this.syncAssuranceLocataireField(
      !!this.form.controls.assuranceBailleur.value,
      false
    );
  }

  private syncAssuranceLocataireField(
    enabled: boolean,
    clearWhenDisabled = true
  ): void {
    const mensuel = this.form.controls.montantMensuelAssuranceLocataire;
    const annuel = this.form.controls.montantAnnuelAssurance;

    if (enabled) {
      mensuel.enable({ emitEvent: false });
      annuel.enable({ emitEvent: false });
    } else {
      mensuel.disable({ emitEvent: false });
      annuel.disable({ emitEvent: false });
      if (clearWhenDisabled) {
        this.form.patchValue(
          {
            montantAnnuelAssurance: '',
            montantMensuelAssuranceLocataire: '',
          },
          { emitEvent: false }
        );
      }
    }
  }

  /** Affiche ou non les champs liés au plafonnement, avec validation. */
  private syncLoyerPlafondFields(
    enabled: boolean,
    clearWhenDisabled = true
  ): void {
    const reference = this.encadrementForm.controls.loyerReference;
    const majore = this.encadrementForm.controls.loyerMajore;

    if (enabled) {
      reference.setValidators([Validators.required]);
      majore.setValidators([Validators.required]);
    } else {
      reference.clearValidators();
      majore.clearValidators();
    }
    reference.updateValueAndValidity({ emitEvent: false });
    majore.updateValueAndValidity({ emitEvent: false });

    if (!enabled && clearWhenDisabled) {
      this.encadrementForm.patchValue(
        {
          loyerReference: '',
          loyerMajore: '',
          complementLoyer: '',
        },
        { emitEvent: false }
      );
    }
  }
}
